import React, { useState } from 'react'
import { Button, Toast } from 'components'
import { updateFleet } from 'api'
import {
  IFleetData,
  IFleetValidationData,
  IParameterValues,
  IRecipientCandidate,
  IValidationTypeData,
  ValidationMode,
} from 'interfaces/IFleetMonitoring'
import strings from 'strings'
import { isViewOnly } from 'tools/auth'
import RecipientsInput from '../../recipientsInput'
import ParametersInput from '../../parametersInput'
import InheritedDefaultsSummary from '../../inheritedDefaultsSummary'
import { hasParameterErrors } from '../../parametersInput/validate'
import styles from './fleetDetail.module.scss'

const FM = strings.FLEET_MONITORING

const sameStringList = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, i) => value === b[i])

interface IFleetDetailProps {
  fleet: IFleetData
  validationTypes: IValidationTypeData[]
  candidates: IRecipientCandidate[]
  onUpdated: () => void | Promise<unknown>
}

const FleetDetail: React.FunctionComponent<IFleetDetailProps> = ({
  fleet,
  validationTypes,
  candidates,
  onUpdated,
}) => {
  const [subTab, setSubTab] = useState<'overrides' | 'recipients'>('overrides')
  const [busy, setBusy] = useState(false)
  // Codes toggled to "On" locally but not yet persisted — used when the type has required
  // thresholds: we don't persist "On" until the params are filled and saved together.
  const [pendingOn, setPendingOn] = useState<Record<string, boolean>>({})
  const readOnly = isViewOnly()

  const validationByCode: Record<string, IFleetValidationData> = {}
  ;(fleet.validations || []).forEach(v => {
    validationByCode[v.code] = v
  })

  const handleApi = async (
    action: () => Promise<unknown>,
    errorMsg: string,
    successMsg?: string,
  ) => {
    setBusy(true)
    try {
      await action()
      // Wait for the fleets reload too, so the control stays disabled until the UI
      // reflects the new value — avoids the "re-enabled but still showing the old mode" gap.
      await onUpdated()
      if (successMsg) Toast({ type: 'success', subTitle: successMsg })
    } catch (e: any) {
      Toast({ type: 'error', subTitle: e?.message || errorMsg })
    } finally {
      setBusy(false)
    }
  }

  const handleModeChange = (code: string, mode: ValidationMode) =>
    handleApi(
      () =>
        updateFleet({
          carrierId: fleet.carrierId,
          validations: [{ code, mode, recipients: validationByCode[code]?.recipients || [] }],
        }),
      FM.ERRORS.UPDATE_OVERRIDE_FAILED,
    )

  const clearPending = (code: string) =>
    setPendingOn(prev => {
      if (!prev[code]) return prev
      const next = { ...prev }
      delete next[code]
      return next
    })

  // Switching to "On" for a type with required thresholds doesn't persist immediately — it stays
  // pending (local) and is saved together with the params via the Settings "Save". Everything else
  // (Off / Inherit / On without required) persists right away.
  const handleModeToggle = (vt: IValidationTypeData, next: ValidationMode) => {
    const code = vt.code
    const persisted = validationByCode[code]?.mode ?? 'inherit'
    const requiresParams = (vt.parameterDefs || []).some(d => d.required)
    if (next === 'on' && requiresParams && persisted !== 'on') {
      setPendingOn(prev => ({ ...prev, [code]: true }))
      return
    }
    clearPending(code)
    handleModeChange(code, next)
  }

  const handleParametersChange = (
    code: string,
    mode: ValidationMode,
    params: IParameterValues,
  ) => {
    // While overriding (On), thresholds must be valid (required filled, in range, etc.) — don't
    // persist an invalid set (the inline error nudges the user). It saves once everything is valid.
    const vt = validationTypes.find(v => v.code === code)
    if (
      vt &&
      hasParameterErrors(vt.parameterDefs, params, {
        level: 'fleet',
        inheritedValues: validationByCode[code]?.inheritedValues,
      })
    )
      return
    handleApi(
      () =>
        updateFleet({
          carrierId: fleet.carrierId,
          validations: [
            {
              code,
              mode,
              recipients: validationByCode[code]?.recipients || [],
              parameters: params,
            },
          ],
        }),
      FM.ERRORS.UPDATE_OVERRIDE_FAILED,
      FM.TOASTS.FLEET_SETTINGS_SAVED,
    )
  }

  const handleEnrollToggle = () =>
    handleApi(
      () =>
        updateFleet({
          carrierId: fleet.carrierId,
          enrollmentStatus: fleet.enrolled ? 'unenrolled' : 'enrolled',
        }),
      FM.ERRORS.ENROLL_FLEET_FAILED(!!fleet.enrolled),
    )

  const handleValidationRecipientsChange = (
    code: string,
    mode: ValidationMode,
    userIds: string[],
  ) => {
    const current = validationByCode[code]?.recipients ?? []
    if (sameStringList(userIds, current)) return
    handleApi(
      () =>
        updateFleet({
          carrierId: fleet.carrierId,
          validations: [{ code, mode, recipients: userIds }],
        }),
      FM.ERRORS.UPDATE_RECIPIENTS_FAILED,
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.subTabs}>
        <button
          type="button"
          className={`${styles.subTab} ${subTab === 'overrides' ? styles.subTabActive : ''}`}
          onClick={() => setSubTab('overrides')}
        >
          {FM.FLEET_DETAIL.TAB_OVERRIDES}
        </button>
        <button
          type="button"
          className={`${styles.subTab} ${subTab === 'recipients' ? styles.subTabActive : ''}`}
          onClick={() => setSubTab('recipients')}
        >
          {FM.FLEET_DETAIL.TAB_RECIPIENTS}
          <span className={styles.subTabCount}>{(fleet.recipients || []).length}</span>
        </button>
      </div>

      {subTab === 'overrides' ? (
        <section>
          <p className={styles.sectionHelper}>
            <b>{FM.FLEET_DETAIL.MODE_INHERIT}</b> follows the company default.{' '}
            <b>{FM.FLEET_DETAIL.MODE_ON}</b> turns this alert on for the fleet even if it’s off
            company-wide. <b>{FM.FLEET_DETAIL.MODE_OFF}</b> turns it off for this fleet.
          </p>
          {validationTypes.length === 0 ? (
            <div className={styles.recipientEmpty}>{FM.COMMON.NO_VALIDATION_TYPES}</div>
          ) : (
            validationTypes.map(vt => {
              const persistedMode = validationByCode[vt.code]?.mode ?? 'inherit'
              // "On" toggled locally but not yet persisted (needs Save together with params).
              const pending = !!pendingOn[vt.code] && persistedMode !== 'on'
              const mode: ValidationMode = pendingOn[vt.code] ? 'on' : persistedMode
              return (
                <div key={vt.code} className={styles.validationCard}>
                  <div className={styles.validationHeader}>
                    <div className={styles.validationInfo}>
                      <span className={styles.validationName}>
                        {vt.name}
                        {vt.severity === 'critical' && (
                          <span className={styles.severityCritical}>{FM.COMMON.CRITICAL}</span>
                        )}
                        {vt.severity === 'high' && (
                          <span className={styles.severityHigh}>{FM.COMMON.HIGH}</span>
                        )}
                        {vt.severity === 'medium' && (
                          <span className={styles.severityMedium}>{FM.COMMON.MEDIUM}</span>
                        )}
                        {vt.severity === 'low' && (
                          <span className={styles.severityLow}>{FM.COMMON.LOW}</span>
                        )}
                      </span>
                      {vt.description && (
                        <div className={styles.validationDescription}>{vt.description}</div>
                      )}
                    </div>
                    <ModeToggle
                      value={mode}
                      disabled={!fleet.enrolled || busy || readOnly}
                      onChange={next => handleModeToggle(vt, next)}
                    />
                  </div>
                  {mode === 'on' && vt.parameterDefs && vt.parameterDefs.length > 0 && (
                    <ParametersInput
                      defs={vt.parameterDefs}
                      value={validationByCode[vt.code]?.parameters ?? {}}
                      inheritedValue={validationByCode[vt.code]?.inheritedValues}
                      inheritedMeta={validationByCode[vt.code]?.inheritedMeta}
                      level="fleet"
                      onChange={params => handleParametersChange(vt.code, mode, params)}
                      disabled={busy || readOnly}
                      manualSave
                      dirtyOverride={pending}
                      deferRequiredErrors={pending}
                      onCancel={() => clearPending(vt.code)}
                    />
                  )}
                  {mode === 'inherit' && (
                    <InheritedDefaultsSummary
                      defs={vt.parameterDefs ?? []}
                      inheritedValue={validationByCode[vt.code]?.inheritedValues}
                      inheritedMeta={validationByCode[vt.code]?.inheritedMeta}
                    />
                  )}
                </div>
              )
            })
          )}
          {!fleet.enrolled && (
            <p className={styles.notEnrolledHint}>{FM.FLEET_DETAIL.NOT_ENROLLED_HINT}</p>
          )}
        </section>
      ) : (
        <section>
          <p className={styles.sectionHelper}>{FM.FLEET_DETAIL.RECIPIENTS_HELPER}</p>
          {validationTypes.length === 0 ? (
            <div className={styles.recipientEmpty}>{FM.COMMON.NO_VALIDATION_TYPES}</div>
          ) : (
            validationTypes.map(vt => {
              const entry = validationByCode[vt.code]
              const mode = entry?.mode ?? 'inherit'
              const recipients = entry?.recipients ?? []
              return (
                <div key={vt.code} className={styles.validationCard}>
                  <div className={styles.validationStack}>
                    <div className={styles.validationInfo}>
                      <span className={styles.validationName}>
                        {vt.name}
                        {vt.severity === 'critical' && (
                          <span className={styles.severityCritical}>{FM.COMMON.CRITICAL}</span>
                        )}
                        {vt.severity === 'high' && (
                          <span className={styles.severityHigh}>{FM.COMMON.HIGH}</span>
                        )}
                      </span>
                      {vt.description && (
                        <div className={styles.validationDescription}>{vt.description}</div>
                      )}
                    </div>
                    <RecipientsInput
                      value={recipients}
                      onChange={userIds =>
                        handleValidationRecipientsChange(vt.code, mode, userIds)
                      }
                      candidates={candidates}
                      disabled={busy || readOnly}
                    />
                  </div>
                </div>
              )
            })
          )}
        </section>
      )}

      {!readOnly && (
        <div className={styles.footer}>
          <span className={styles.footerHint}>{FM.FLEET_DETAIL.ENROLLMENT_FOOTER}</span>
          <Button
            label={fleet.enrolled ? FM.FLEET_DETAIL.UNENROLL : FM.FLEET_DETAIL.ENROLL}
            variant={fleet.enrolled ? 'outlined' : 'contained'}
            onClick={handleEnrollToggle}
          />
        </div>
      )}
    </div>
  )
}

interface IModeToggleProps {
  value: ValidationMode
  disabled?: boolean
  onChange: (next: ValidationMode) => void
}

const ModeToggle: React.FC<IModeToggleProps> = ({ value, disabled, onChange }) => {
  const options: { key: ValidationMode; label: string; activeClass: string }[] = [
    { key: 'off', label: FM.FLEET_DETAIL.MODE_OFF, activeClass: styles.modeButtonActiveOff },
    { key: 'inherit', label: FM.FLEET_DETAIL.MODE_INHERIT, activeClass: styles.modeButtonActiveInherit },
    { key: 'on', label: FM.FLEET_DETAIL.MODE_ON, activeClass: styles.modeButtonActiveOn },
  ]
  return (
    <div className={styles.modeToggle}>
      {options.map(o => (
        <button
          key={o.key}
          type="button"
          disabled={disabled}
          onClick={() => value !== o.key && onChange(o.key)}
          className={`${styles.modeButton} ${value === o.key ? o.activeClass : ''}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export default FleetDetail
